<template>
  <div class="more-actions-dropdown" ref="dropdownRef">
    <button
      ref="triggerRef"
      :class="['more-action-tab', { active: isAnySecondaryActive }]"
      @click.stop="toggleDropdown"
      :title="t('projects.moreActions')"
      :aria-label="t('projects.moreActions')"
      :aria-expanded="isOpen"
      :aria-haspopup="true"
      type="button"
    >
      <span class="tab-icon" style="font-size: 1.4em;">
        ⋯
      </span>
      <span class="tab-label">{{ t('projects.more') }}</span>
      <span class="dropdown-indicator">
        <i :class="['fas', isOpen ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
      </span>
      <span class="tab-highlight" v-if="isAnySecondaryActive"></span>
    </button>

    <!-- Teleport backdrop and menu to body to escape the transform containing block of .nav-tabs -->
    <teleport to="body">
      <!-- Backdrop -->
      <transition name="backdrop-fade">
        <div v-if="isOpen" class="mad-backdrop" @click="closeDropdown"></div>
      </transition>

      <!-- Dropdown Menu -->
      <transition name="dropdown-fade">
        <div v-if="isOpen" class="mad-menu" role="menu" @click.stop :style="menuStyle">
        <div class="dropdown-content">
          <!-- Search bar for many items -->
          <div v-if="tabs.length > 8" class="dropdown-search">
            <i class="fas fa-search search-icon"></i>
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="t('projects.searchActions')"
              class="search-input"
              @click.stop
            />
          </div>

          <!-- Grouped or flat list based on enableGrouping prop -->
          <template v-if="enableGrouping && groupedTabs.length > 0">
            <div v-for="group in filteredGroupedTabs" :key="group.category" class="dropdown-group">
              <div class="group-label">{{ group.label }}</div>
              <template v-for="tab in group.tabs" :key="tab.name || tab.id">
                <PermissionGuard 
                  :permission="getTabSinglePermission(tab)"
                  :any-permissions="getTabMultiplePermissions(tab)"
                  :project-id="projectId"
                >
                  <button
                    :class="['dropdown-item', { active: activeTab === (tab.name || tab.id) }]"
                    @click="selectTab(tab.name || tab.id)"
                    role="menuitem"
                  >
                    <span class="item-icon" v-html="tab.icon"></span>
                    <span class="item-label">{{ tab.label }}</span>
                    <span v-if="tab.badge" class="item-badge">{{ tab.badge }}</span>
                    <i v-if="activeTab === (tab.name || tab.id)" class="fas fa-check item-check"></i>
                  </button>
                </PermissionGuard>
              </template>
            </div>
          </template>

          <!-- Flat list (default) -->
          <template v-else>
            <template v-for="tab in filteredTabs" :key="tab.name || tab.id">
              <PermissionGuard 
                :permission="getTabSinglePermission(tab)"
                :any-permissions="getTabMultiplePermissions(tab)"
                :project-id="projectId"
              >
                <button
                  :class="['dropdown-item', { active: activeTab === (tab.name || tab.id) }]"
                  @click="selectTab(tab.name || tab.id)"
                  role="menuitem"
                >
                  <span class="item-icon" v-html="tab.icon"></span>
                  <span class="item-label">{{ tab.label }}</span>
                  <span v-if="tab.badge" class="item-badge">{{ tab.badge }}</span>
                  <i v-if="activeTab === (tab.name || tab.id)" class="fas fa-check item-check"></i>
                </button>
              </PermissionGuard>
            </template>
          </template>

          <!-- No results message -->
          <div v-if="filteredTabs.length === 0 && searchQuery" class="no-results">
            <i class="fas fa-search"></i>
            <p>{{ t('projects.noActionsFound') }}</p>
          </div>
        </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import PermissionGuard from '@/components/common/PermissionGuard.vue';

interface Tab {
  id: string;
  name?: string;
  label: string;
  permission?: string | string[];
  permissions?: string[];
  title?: string;
  icon?: string;
  badge?: string | number;
  category?: string;
  [key: string]: any;
}

interface GroupedTab {
  category: string;
  label: string;
  tabs: Tab[];
}

interface Props {
  tabs: Tab[];
  activeTab?: string;
  projectId: string | number;
  enableGrouping?: boolean;
}

interface Emits {
  select: [tabId: string];
}

const props = withDefaults(defineProps<Props>(), {
  activeTab: '',
  enableGrouping: false
});

const emit = defineEmits<Emits>();
const { t } = useI18n();

const dropdownRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);

// Constants
const MOBILE_BREAKPOINT = 768;
const DROPDOWN_SPACING = 4; // px gap between trigger and desktop menu

// Track whether we're in mobile view and the dropdown's desktop position
const isMobileView = ref(false);
const menuPosition = ref({ top: 0, right: 0 });

// Compute the inline style for the menu:
// - On mobile the CSS (bottom-sheet) takes over, so we return nothing.
// - On desktop we use fixed positioning anchored to the trigger button.
const menuStyle = computed<Record<string, string>>(() => {
  if (isMobileView.value) return {};
  return {
    top: `${menuPosition.value.top}px`,
    right: `${menuPosition.value.right}px`,
  };
});

// Recalculate desktop position from the trigger button's bounding rect
const updateMenuPosition = () => {
  isMobileView.value = window.innerWidth <= MOBILE_BREAKPOINT;
  if (!isMobileView.value && triggerRef.value) {
    const rect = triggerRef.value.getBoundingClientRect();
    menuPosition.value = {
      top: rect.bottom + DROPDOWN_SPACING,
      right: window.innerWidth - rect.right,
    };
  }
};

// Helper functions to handle permission arrays
const getTabSinglePermission = (tab: Tab) => {
  // Get permissions from either 'permission', 'permissions', or both
  const tabPermissions: string[] = [];
  
  if (tab.permission) {
    if (Array.isArray(tab.permission)) {
      tabPermissions.push(...tab.permission);
    } else {
      tabPermissions.push(tab.permission);
    }
  }
  
  if (tab.permissions) {
    tabPermissions.push(...tab.permissions);
  }
  
  // If only one permission, return it for single permission check
  return tabPermissions.length === 1 ? tabPermissions[0] : null;
};

const getTabMultiplePermissions = (tab: Tab) => {
  // Get permissions from either 'permission', 'permissions', or both
  const tabPermissions: string[] = [];
  
  if (tab.permission) {
    if (Array.isArray(tab.permission)) {
      tabPermissions.push(...tab.permission);
    } else {
      tabPermissions.push(tab.permission);
    }
  }
  
  if (tab.permissions) {
    tabPermissions.push(...tab.permissions);
  }
  
  // If multiple permissions, return them for anyPermissions check
  return tabPermissions.length > 1 ? tabPermissions : [];
};
const isOpen = ref(false);
const searchQuery = ref('');

// Check if any secondary tab is active
const isAnySecondaryActive = computed(() => {
  return props.tabs.some((tab: Tab) => (tab.name || tab.id) === props.activeTab);
});

// Filter tabs based on search query
const filteredTabs = computed(() => {
  if (!searchQuery.value) return props.tabs;
  
  const query = searchQuery.value.toLowerCase();
  return props.tabs.filter((tab: Tab) => 
    tab.label.toLowerCase().includes(query) ||
    (tab.title && tab.title.toLowerCase().includes(query))
  );
});

// Group tabs by category
const groupedTabs = computed((): GroupedTab[] => {
  if (!props.enableGrouping) return [];
  
  const groups: { [key: string]: GroupedTab } = {};
  
  props.tabs.forEach((tab: Tab) => {
    const category = tab.category || 'other';
    if (!groups[category]) {
      groups[category] = {
        category,
        label: getCategoryLabel(category),
        tabs: []
      };
    }
    groups[category].tabs.push(tab);
  });
  
  return Object.values(groups);
});

// Filter grouped tabs based on search
const filteredGroupedTabs = computed(() => {
  if (!searchQuery.value) return groupedTabs.value;
  
  const query = searchQuery.value.toLowerCase();
  return groupedTabs.value
    .map((group: GroupedTab) => ({
      ...group,
      tabs: group.tabs.filter((tab: Tab) =>
        tab.label.toLowerCase().includes(query) ||
        (tab.title && tab.title.toLowerCase().includes(query))
      )
    }))
    .filter((group: GroupedTab) => group.tabs.length > 0);
});

// Get category label
const getCategoryLabel = (category: string): string => {
  const labels: { [key: string]: string } = {
    collaboration: t('projects.categories.collaboration'),
    management: t('projects.categories.management'),
    finance: t('projects.categories.finance'),
    settings: t('projects.categories.settings'),
    other: t('projects.categories.other')
  };
  return labels[category] || category;
};

// Toggle dropdown
const toggleDropdown = () => {
  if (!isOpen.value) {
    updateMenuPosition();
    searchQuery.value = '';
  }
  isOpen.value = !isOpen.value;
};

// Close dropdown
const closeDropdown = () => {
  isOpen.value = false;
  searchQuery.value = '';
};

// Select tab
const selectTab = (tabId: string) => {
  emit('select', tabId);
  closeDropdown();
};

// Click outside handler
const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    closeDropdown();
  }
};

// Keyboard handler
const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpen.value) {
    closeDropdown();
  }
};

// Resize handler – debounced to avoid excessive DOM work during resize
let resizeTimer: ReturnType<typeof setTimeout> | null = null;
const handleResize = () => {
  if (resizeTimer !== null) clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    isMobileView.value = window.innerWidth <= MOBILE_BREAKPOINT;
    if (isOpen.value) {
      closeDropdown();
    }
    resizeTimer = null;
  }, 100);
};

// Watch for active tab changes
watch(() => props.activeTab, () => {
  // Close dropdown when a tab is selected externally
  if (isOpen.value) {
    closeDropdown();
  }
});

onMounted(() => {
  isMobileView.value = window.innerWidth <= MOBILE_BREAKPOINT;
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleKeyDown);
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('resize', handleResize);
  if (resizeTimer !== null) clearTimeout(resizeTimer);
});
</script>

<style scoped>
.more-actions-dropdown {
  position: relative;
  display: inline-block;
  z-index: 1;
}

.more-action-tab {
  flex: 1;
  padding: 12px 20px;
  border: none;
  background: transparent;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  outline: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  white-space: nowrap;
  height: auto;
  min-height: auto;
  line-height: 1;
}

.more-action-tab:hover {
  background: #f8fafc;
  color: #475569;
}

.more-action-tab.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.tab-icon {
  margin-right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;
  flex-shrink: 0;
}

.tab-icon svg,
.tab-icon i {
  height: 18px;
  width: 18px;
  transition: none;
}

.tab-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: none;
  text-align: center;
  line-height: 1;
  flex-shrink: 1;
}

.dropdown-indicator {
  margin-left: 4px;
  font-size: 0.75rem;
  opacity: 0.7;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.more-action-tab:hover .dropdown-indicator {
  opacity: 1;
}

.more-action-tab.active .dropdown-indicator {
  opacity: 1;
  color: white;
}

.tab-highlight {
  display: none;
}

/* ── Teleported elements (backdrop + menu) ────────────────────────────────── */
/* These are rendered at the <body> level via <teleport> so that CSS transforms
   on ancestor elements (e.g. .nav-tabs translateX) do not create a new
   containing block and break position:fixed behaviour on iOS/Safari. */

/* Backdrop */
.mad-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: 10050;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

/* Dropdown Menu – desktop: top/right come from inline :style binding */
.mad-menu {
  position: fixed;
  min-width: 240px;
  max-width: 320px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 10051;
  overflow: hidden;
}


.dropdown-content {
  max-height: 480px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 0.5rem 0;
}

/* Search */
.dropdown-search {
  padding: 0.5rem;
  border-bottom: 1px solid var(--neutral-200);
  position: relative;
  margin-bottom: 0.5rem;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-tertiary);
  font-size: 0.875rem;
}

.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  border: 1px solid var(--neutral-200);
  border-radius: var(--border-radius-sm, 6px);
  font-size: 0.875rem;
  outline: none;
  transition: all 0.2s ease;
}

.search-input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb, 0, 100, 255), 0.1);
}

/* Dropdown Groups */
.dropdown-group {
  margin-bottom: 0.5rem;
}

.dropdown-group:last-child {
  margin-bottom: 0;
}

.group-label {
  padding: 0.5rem 1rem 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Dropdown Items */
.dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.625rem 1rem;
  border: none;
  background: none;
  color: var(--text-primary);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  gap: 0.75rem;
}

.dropdown-item:hover {
  background-color: rgba(var(--primary-rgb, 0, 100, 255), 0.06);
  color: var(--primary);
}

.dropdown-item.active {
  background-color: rgba(var(--primary-rgb, 0, 100, 255), 0.1);
  color: var(--primary);
  font-weight: 600;
}

.item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.item-icon svg,
.item-icon i {
  width: 16px;
  height: 16px;
}

.item-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 0.375rem;
  background-color: var(--primary);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 10px;
  flex-shrink: 0;
}

.item-check {
  color: var(--primary);
  font-size: 0.875rem;
  flex-shrink: 0;
}

/* No Results */
.no-results {
  padding: 2rem 1rem;
  text-align: center;
  color: var(--text-tertiary);
}

.no-results i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  opacity: 0.5;
}

.no-results p {
  font-size: 0.875rem;
  margin: 0;
}

/* Transitions */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.backdrop-fade-enter-active,
.backdrop-fade-leave-active {
  transition: all 0.2s ease;
}

.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .mad-backdrop {
    background: rgba(0, 0, 0, 0.3);
  }

  /* Bottom-sheet on mobile: override the inline :style top/right values */
  .mad-menu {
    top: auto !important;
    right: auto !important;
    bottom: 0;
    left: 0;
    width: 100%;
    max-width: 100%;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.18);
  }

  .dropdown-content {
    max-height: 60vh;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    /* Leave room for the iOS home indicator */
    padding-bottom: calc(0.5rem + env(safe-area-inset-bottom, 0px));
  }

  .dropdown-fade-enter-from {
    opacity: 1;
    transform: translateY(100%);
  }

  .dropdown-fade-leave-to {
    opacity: 1;
    transform: translateY(100%);
  }
  
  .more-action-tab {
    padding: 12px 16px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .tab-label {
    display: none;
  }

  .tab-icon {
    margin-right: 0;
    height: 20px;
    width: 20px;
  }

  .dropdown-indicator {
    display: none;
  }

  .more-action-tab {
    min-width: 48px;
    padding: 12px 16px;
  }
  
  .more-action-tab.active {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
}
</style>
